import { AssignProjectManagerDialog } from "@components/table/members/remove-xg-seat/assign-project-manager-dialog";
import { getMemberProjectDetails, MemberName, RemoveXgSeatList } from "@components/table/members/remove-xg-seat/remove-xg-seat-list";
import { getProjectChangeBlockReason, isProjectManager, PROJECTS_FAILED_MESSAGE } from "@components/table/members/remove-xg-seat/remove-xg-seat-utils";
import { useChangeMemberProjects } from "@components/table/members/remove-xg-seat/use-change-member-projects";
import { useMemberEditorProjects } from "@components/table/members/remove-xg-seat/use-member-groups-with-details";
import { usePendingIds } from "@components/table/members/remove-xg-seat/use-pending-ids";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useState } from "react";

/**
 * Step of taking a Sphere XG seat away from a member that lists the projects the member manages.
 * A project manager cannot be removed from a project or be made a viewer, so the user chooses a
 * new manager for a project, or an admin in the workspace for all of them at once, on a plan that
 * lets them choose project managers. The user also chooses whether the member stays in the
 * projects as a viewer, or else leaves them.
 */
export function RemoveXgSeatManagedProjectsStep({
  member,
  onBusyChange,
  onResolvedChange
}) {
  const {
    projects,
    status
  } = useMemberEditorProjects(member.identity);
  const {
    pendingIds,
    runWhilePending
  } = usePendingIds(onBusyChange);
  const {
    settleFormerManager
  } = useChangeMemberProjects({
    member
  });
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  // The projects whose new project manager the user is choosing
  const [projectsToHandOver, setProjectsToHandOver] = useState(null);
  const managedProjects = projects.filter(({
    project
  }) => isProjectManager(project));
  const name = <MemberName member={member} />;
  return <>
      <RemoveXgSeatList status={status} description={<>
            {name} manages these projects. A new project manager is required for
            all of the projects listed before.
          </>} isGoOnHintShown={false} emptyMessage={<>{name} does not manage any project.</>} failedMessage={PROJECTS_FAILED_MESSAGE} items={managedProjects.map(toItem)} pendingIds={pendingIds} onResolvedChange={onResolvedChange} bulkActions={[{
      label: "Change project manager in all",
      onClick: () => setProjectsToHandOver(managedProjects)
    }]} />

      {projectsToHandOver && <AssignProjectManagerDialog member={member} projects={projectsToHandOver} onClose={() => setProjectsToHandOver(null)} onAssigned={(shouldKeepAsViewer, assignedProjects) => {
      setProjectsToHandOver(null);
      void settleFormerManagers(assignedProjects, shouldKeepAsViewer);
    }} />}
    </>;

  /** Returns how a project the member manages is listed */
  function toItem(memberProject) {
    return {
      id: memberProject.project.id,
      name: memberProject.project.name,
      details: getMemberProjectDetails(memberProject),
      blockReason: getProjectChangeBlockReason(memberProject.project, canAssignManagers),
      actions: [{
        label: "Change project manager",
        onClick: () => setProjectsToHandOver([memberProject])
      }]
    };
  }

  /**
   * Makes the member a viewer in the projects they no longer manage, or removes them from them.
   * This runs one project after the other, so that fetching a group again after one of them cannot
   * overwrite a change in another project of the same group.
   */
  async function settleFormerManagers(projectsToSettle, shouldKeepAsViewer_0) {
    await runWhilePending(projectsToSettle.map(({
      project: project_0
    }) => project_0.id), async () => {
      for (const memberProject_0 of projectsToSettle) {
        await settleFormerManager(memberProject_0, shouldKeepAsViewer_0);
      }
    });
  }
}